import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { profileRepository } from "@/repositories/profileRepository";
import { settingsRepository } from "@/repositories/settingsRepository";

/**
 * Query keys, in one place.
 *
 * Invalidation is the whole reason: creating a device must refresh the device
 * list, and a key typed by hand in two files eventually diverges by one
 * character and the list silently stops updating.
 */
export const settingsKeys = {
  merchant: ["settings", "merchant"] as const,
  settings: ["settings", "operational"] as const,
  branches: ["settings", "branches"] as const,
  sections: ["settings", "sections"] as const,
  staff: ["settings", "staff"] as const,
  roles: ["settings", "roles"] as const,
  devices: ["settings", "devices"] as const,
  tills: ["settings", "tills"] as const,
};

/**
 * Kept out of `settingsKeys` on purpose.
 *
 * Everything above belongs to a merchant and is invalidated when a merchant
 * changes. A profile belongs to a person and survives that, so filing it under
 * the same prefix would mean a broad `["settings"]` invalidation silently
 * discarding it too.
 */
export const profileKeys = {
  profile: ["profile", "me"] as const,
};

export const useProfile = () =>
  useQuery({ queryKey: profileKeys.profile, queryFn: profileRepository.get });

export const useMerchant = () =>
  useQuery({ queryKey: settingsKeys.merchant, queryFn: settingsRepository.merchant });

export const useOperationalSettings = () =>
  useQuery({ queryKey: settingsKeys.settings, queryFn: settingsRepository.settings });

export const useBranches = () =>
  useQuery({ queryKey: settingsKeys.branches, queryFn: settingsRepository.branches });

export const useSections = () =>
  useQuery({ queryKey: settingsKeys.sections, queryFn: settingsRepository.sections });

export const useStaff = () =>
  useQuery({ queryKey: settingsKeys.staff, queryFn: settingsRepository.staff });

export const useRoles = () =>
  useQuery({ queryKey: settingsKeys.roles, queryFn: settingsRepository.roles });

export const useDevices = () =>
  useQuery({ queryKey: settingsKeys.devices, queryFn: settingsRepository.devices });

export const useTills = () =>
  useQuery({ queryKey: settingsKeys.tills, queryFn: settingsRepository.tills });

/** Invalidate one area after a mutation. */
export function useInvalidate(key: readonly unknown[]) {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: key });
}

export { useMutation };
